import React, { useEffect } from 'react';
import { Box } from '@opendoor/bricks/core';

import { FormRadioInput, NO, YES } from 'sellDirect/components/closingDash/form/FormRadioInput';
import { FormTextInput } from 'sellDirect/components/closingDash/form/FormTextInput';

import { IResetableFormRenderProps } from 'sellDirect/components/closingDash/ResetableForm';
import {
  LAST_FOUNDATION_EVALUATION_OPTIONS,
  SPRINKLER_TIMER_OPTIONS,
} from 'sellDirect/helpers/closingDash/disclosures';
import { UnderContractOffer } from 'sellDirect/OfferContext';
import { text } from 'sellDirect/components/closingDash/content';
import { DisclosuresFormState } from 'sellDirect/components/closingDash/disclosures/DisclosuresForm';

interface IProps {
  offer: UnderContractOffer;
  loading?: boolean;
  resetableForm: IResetableFormRenderProps;
}

/**
 * Editable HVAC and roof form.
 *
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const HvacRoofFormContent: React.FC<IProps> = ({ offer, resetableForm, loading }) => {
  const shouldAskPolybutenePiping = offer.closingExperience?.shouldAskPolybutenePiping;
  const shouldAskStormRoofDamage = offer.closingExperience?.shouldAskStormAndFoundation;

  const { onUpdate, onUpdateMany } = resetableForm;
  const formState: DisclosuresFormState = resetableForm.formState;
  const { hasSprinklerOrIrrigation, filedClaim } = formState;

  useEffect(() => {
    if (hasSprinklerOrIrrigation === NO) {
      onUpdateMany([
        { id: 'workingSprinkler', value: undefined },
        { id: 'sprinklerTimerType', value: undefined },
      ]);
    }
    // eslint-disable-next-line
  }, [hasSprinklerOrIrrigation]);

  useEffect(() => {
    if (filedClaim === NO) {
      onUpdateMany([
        { id: 'damageClaimDetails', value: undefined },
        { id: 'receivedProceeds', value: undefined },
      ]);
    }
    // eslint-disable-next-line
  }, [filedClaim]);

  return (
    <>
      <FormRadioInput
        disabled={loading}
        inputName="hasSumpPump"
        title={text.disclosuresPage.hvacRoofInfo.hasSumpPump(offer.shouldSeeAgentExperience)}
        content={formState?.hasSumpPump}
        onUpdate={onUpdate}
        mb={3}
      />
      <FormRadioInput
        disabled={loading}
        inputName="hasSprinklerOrIrrigation"
        title={text.disclosuresPage.hvacRoofInfo.hasSprinkler(offer.shouldSeeAgentExperience)}
        content={formState?.hasSprinklerOrIrrigation}
        onUpdate={onUpdate}
        mb={3}
      />
      {formState?.hasSprinklerOrIrrigation === YES && (
        <Box ml={8}>
          <FormRadioInput
            disabled={loading}
            inputName="sprinklerTimerType"
            title={text.disclosuresPage.hvacRoofInfo.sprinklerTimerType}
            content={formState?.sprinklerTimerType}
            onUpdate={onUpdate}
            options={SPRINKLER_TIMER_OPTIONS}
            mb={3}
          />
          <FormRadioInput
            disabled={loading}
            inputName="workingSprinkler"
            title={text.disclosuresPage.hvacRoofInfo.sprinklerInWorkingOrder}
            content={formState?.workingSprinkler}
            onUpdate={onUpdate}
            mb={3}
          />
        </Box>
      )}
      <FormRadioInput
        disabled={loading}
        inputName="filedClaim"
        title={text.disclosuresPage.hvacRoofInfo.hasFiledInsuranceClaim(
          offer.shouldSeeAgentExperience,
        )}
        content={formState?.filedClaim}
        onUpdate={onUpdate}
        mb={3}
      />
      {formState?.filedClaim === YES && (
        <Box ml={8}>
          <FormTextInput
            disabled={loading}
            inputName="damageClaimDetails"
            title={text.disclosuresPage.hvacRoofInfo.claimDetails}
            content={formState?.damageClaimDetails}
            onUpdate={onUpdate}
          />
          <FormRadioInput
            disabled={loading}
            inputName="receivedProceeds"
            title={text.disclosuresPage.hvacRoofInfo.receivedProceedsFromClaim(
              offer.shouldSeeAgentExperience,
            )}
            content={formState?.receivedProceeds}
            onUpdate={onUpdate}
            mb={3}
          />
        </Box>
      )}
      {shouldAskPolybutenePiping && (
        <FormRadioInput
          disabled={loading}
          inputName="hasPolybutenePiping"
          title={text.disclosuresPage.hvacRoofInfo.hasPolybutenePiping(
            offer.shouldSeeAgentExperience,
          )}
          content={formState?.hasPolybutenePiping}
          onUpdate={onUpdate}
          mb={3}
        />
      )}
      {shouldAskStormRoofDamage && (
        <>
          <FormRadioInput
            disabled={loading}
            inputName="roofStormDamage"
            title={text.disclosuresPage.hvacRoofInfo.hasRoofStormDamage(
              offer.shouldSeeAgentExperience,
            )}
            content={formState?.roofStormDamage}
            onUpdate={onUpdate}
          />
          <FormRadioInput
            disabled={loading}
            inputName="lastFoundationEvaluation"
            title={text.disclosuresPage.hvacRoofInfo.lastFoundationEvaluation(
              offer.shouldSeeAgentExperience,
            )}
            content={formState?.lastFoundationEvaluation}
            onUpdate={onUpdate}
            options={LAST_FOUNDATION_EVALUATION_OPTIONS}
          />
        </>
      )}
      <FormRadioInput
        disabled={loading}
        inputName="hasWellWaterSupply"
        title={text.disclosuresPage.hvacRoofInfo.hasWellWaterSupply(offer.shouldSeeAgentExperience)}
        content={formState?.hasWellWaterSupply}
        onUpdate={onUpdate}
      />
    </>
  );
};
